import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { confirmUsername, requestResetLink } from "../../../redux/actions/user.actions";
import { clearUserError } from "../../../redux/reducers/user.reducer";
import { AppDispatch } from "../../../redux/store";
import RootState from "../../../redux/states/root.state";
import { PersonIcon } from "../../../components/common/Icons/Icons";
import AuthAlert from "./AuthAlert";

/*
 * Two ways through:
 *   username -> email           (the user knows their username)
 *   forgot username -> email    (email only; the auth service either sends the reset
 *                                link, or emails the list of usernames when several
 *                                accounts share the address)
 * Links in the "Your usernames" email open this page with ?username=&email= and land
 * straight on the email step with the username fixed. The user still has to press
 * Send: auto-submitting would let mail scanners that prefetch links trigger resets.
 */

type Step = "username" | "email";
type Mode = "with-username" | "email-only";

/** How long after a send before "Send again" unlocks. Matches the auth service's 60s throttle. */
const RESEND_AFTER_MS = 60_000;

export interface ForgotPasswordCopy {
  title: string;
  subtitle: React.ReactNode;
}

/** Heading and sub-line for the current step; the page renders them in the card header. */
export const forgotPasswordCopy = (step: Step, mode: Mode, fromEmailLink: boolean): ForgotPasswordCopy => {
  if (step === "username") {
    return { title: "Reset your password", subtitle: "Start with your username. We'll ask for your email next." };
  }
  if (mode === "email-only") {
    return {
      title: "Find your account",
      subtitle: "Enter the email address on your account. If more than one account uses it, we'll email you your usernames.",
    };
  }
  return {
    title: "Reset your password",
    subtitle: fromEmailLink
      ? "Confirm the email address for this account and we'll send the reset link."
      : "Enter the email address on this account and we'll send the reset link.",
  };
};

interface ForgotPasswordFormProps {
  /** Lets the page show the step's heading in the card header. */
  onCopyChange?: (copy: ForgotPasswordCopy) => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onCopyChange }) => {
  const [searchParams] = useSearchParams();
  const presetUsername = (searchParams.get("username") ?? "").trim();
  const presetEmail = (searchParams.get("email") ?? "").trim();
  const arrivedFromEmailLink = presetUsername !== "";

  const [username, setUsername] = useState(presetUsername);
  const [email, setEmail] = useState(presetEmail);
  const [message, setMessage] = useState("");
  const [step, setStep] = useState<Step>(arrivedFromEmailLink ? "email" : "username");
  const [mode, setMode] = useState<Mode>("with-username");
  const [fromEmailLink, setFromEmailLink] = useState(arrivedFromEmailLink);
  // When the last email went out (null until one has), and a clock to drive the resend countdown
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const error = useSelector((state: RootState) => state.user.error);
  const loading = useSelector((state: RootState) => state.user.loading);
  const dispatch = useDispatch<AppDispatch>();

  // Moving between steps (or arriving here from the login page) starts clean:
  // no success message and no error left over from the previous step.
  useEffect(() => {
    setMessage("");
    setSentAt(null);
    dispatch(clearUserError());
  }, [step, mode, dispatch]);

  // Tick once a second while the resend countdown is running
  useEffect(() => {
    if (sentAt === null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [sentAt]);

  const resendInSeconds = sentAt === null ? 0 : Math.max(0, Math.ceil((sentAt + RESEND_AFTER_MS - now) / 1000));
  const canResend = sentAt !== null && resendInSeconds === 0;

  useEffect(() => {
    onCopyChange?.(forgotPasswordCopy(step, mode, fromEmailLink));
  }, [step, mode, fromEmailLink, onCopyChange]);

  const handleConfirmUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(confirmUsername({ username }));

    if (confirmUsername.fulfilled.match(result)) {
      setMode("with-username");
      setStep("email");
    }
  };

  const handleForgotUsername = () => {
    setUsername("");
    setFromEmailLink(false);
    setMode("email-only");
    setStep("email");
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = mode === "with-username" ? { email, username } : { email };
    const result = await dispatch(requestResetLink(payload));

    if (!requestResetLink.fulfilled.match(result)) {
      return;
    }

    if (result.payload.multiple_accounts) {
      setMessage(
        "More than one account uses this email address. We've emailed you the list of usernames, each with its own reset link."
      );
    } else if (result.payload.username) {
      setMessage(`Password reset link sent for account "${result.payload.username}". Check your email.`);
    } else {
      setMessage("Password reset link has been sent. Check your email.");
    }
    // Keep the email in the field so "Send again" can reuse it
    setSentAt(Date.now());
  };

  const handleBack = () => {
    setStep("username");
    setMode("with-username");
    setFromEmailLink(false);
    setEmail("");
  };

  if (step === "username") {
    return (
      <form className="auth-form" onSubmit={handleConfirmUsername} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="username">Username</label>
          <input
            id="username"
            name="username"
            className="input"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        {error && <AuthAlert kind="error" message={error} />}

        <div className="auth-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <span className="spinner auth-btn-spinner" aria-label="Checking username" /> : "Continue"}
          </button>
        </div>

        <p className="auth-footer">
          <button type="button" id="forgot-username" className="auth-link" onClick={handleForgotUsername}>
            Forgot your username?
          </button>
        </p>
        <p className="auth-footer">
          <Link to="/login" className="auth-link">Back to log in</Link>
        </p>
      </form>
    );
  }

  return (
    <form className="auth-form" onSubmit={handleRequestReset} noValidate>
      {mode === "with-username" && (
        <span className="auth-account">
          <PersonIcon />
          {username}
        </span>
      )}

      <div className="field">
        <label className="field-label" htmlFor="email">Email address</label>
        <input
          id="email"
          name="email"
          className="input"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {error && <AuthAlert kind="error" message={error} />}
      {message && <AuthAlert kind="success" message={message} />}

      {sentAt !== null && (
        <p className="auth-hint" aria-live="polite">
          Not there yet? Give it a minute and check your spam or junk folder.
          {canResend
            ? " Still nothing? Use Send again below."
            : ` You can send it again in ${resendInSeconds}s.`}
        </p>
      )}

      <div className="auth-actions">
        <button type="button" id="forgot-back" className="btn btn-ghost" onClick={handleBack} disabled={loading}>
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || (sentAt !== null && !canResend)}>
          {loading ? (
            <span className="spinner auth-btn-spinner" aria-label="Sending" />
          ) : sentAt !== null ? (
            "Send again"
          ) : (
            "Send"
          )}
        </button>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
